import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash-es';

import collectionRepository from '@/features/collections/repositories/CollectionRepository';
import { useCollectionStore } from '@/features/collections/stores/useCollectionStore';
import type { DisplayedCollectionSection } from '@/features/collections/types/collection.types';
import pageRepository from '@/features/pages/repositories/PageRepository';
import { collectionKeys, invalidatePageQueries } from '@/shared/query/queryKeys';

/**
 * Mutations for collection section/page ordering.
 *
 * Dragging pages between sections updates collection section metadata and page
 * metadata together. The structure is written to the query cache first so
 * drag-and-drop feels instant, then persisted and re-validated.
 */
export function useCollectionStructureActions() {
  const queryClient = useQueryClient();
  const currentCollection = useCollectionStore(state => state.currentCollection);
  const setCurrentCollection = useCollectionStore(state => state.setCurrentCollection);

  const updateStructureItem = useCallback(
    async (newStructure: DisplayedCollectionSection[]) => {
      if (!currentCollection) return;

      const structureKey = collectionKeys.structure(currentCollection.id);
      const previousStructure =
        queryClient.getQueryData<DisplayedCollectionSection[]>(structureKey) ?? [];
      queryClient.setQueryData(structureKey, newStructure);

      const newSections = newStructure.map(s => s.section);
      const oldSections = currentCollection.sections || [];
      const hasSectionsChanged = !isEqual(newSections, oldSections);

      if (hasSectionsChanged) {
        await collectionRepository.updateCollection(currentCollection.id, {
          sections: [
            ...newSections,
            ...oldSections
              .filter(os => !newSections.map(nS => nS.id).includes(os.id))
              .map(os => ({ ...os, archivedAt: new Date().getTime() })),
          ],
        });

        setCurrentCollection({
          ...currentCollection,
          sections: newSections,
        });
      }

      const allPages = newStructure.map(s => s.section.pages).flat();
      const pagesMap = new Map(
        previousStructure.flatMap(s => s.section.pages).map(page => [page.id, page])
      );

      for (const page of allPages) {
        const oldPage = pagesMap.get(page.id);
        if (isEqual(oldPage, page)) continue;
        await pageRepository.updatePage(page.id, {
          sectionId: page.sectionId,
          orderNumber: page.orderNumber,
        });
      }

      // structure() keys nest under detail(), so this refreshes both
      void queryClient.invalidateQueries({ queryKey: collectionKeys.detail(currentCollection.id) });
      void invalidatePageQueries(queryClient);
    },
    [currentCollection, queryClient, setCurrentCollection]
  );

  const archiveCollectionSection = useCallback(
    async (sectionId: string) => {
      if (!currentCollection) return;

      await collectionRepository.archiveCollectionData(currentCollection, sectionId);
      await queryClient.invalidateQueries({
        queryKey: collectionKeys.detail(currentCollection.id),
      });
      await invalidatePageQueries(queryClient);
    },
    [currentCollection, queryClient]
  );

  return {
    updateStructureItem,
    archiveCollectionSection,
  };
}
